import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { BookAppointmentDetails } from "./BookAppointmentDetails";
import { BookingsList } from "./BookingsList";

interface Props {
  patientId: string;
  facilityId?: string;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  /** When true the sheet opens automatically on first render. */
  defaultOpen?: boolean;
}

const PENDING_BOOKING_STORAGE_KEY = (facilityId: string, patientId: string) =>
  `opd_pending_booking:${facilityId}:${patientId}`;

export default function BookAppointmentSheet({
  patientId,
  facilityId,
  trigger,
  onSuccess,
  defaultOpen = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { t } = useTranslation();

  const hasPendingBooking = useMemo(() => {
    if (!facilityId) return false;

    try {
      return !!sessionStorage.getItem(
        PENDING_BOOKING_STORAGE_KEY(facilityId, patientId),
      );
    } catch {
      return false;
    }
  }, [facilityId, patientId]);

  // If defaultOpen changes from false→true after mount (e.g. patient data
  // loaded on VerifyPatient and a pending booking was detected), open the sheet.
  useEffect(() => {
    if (defaultOpen || hasPendingBooking) setIsOpen(true);
  }, [defaultOpen, hasPendingBooking]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="md:w-[90%] !max-w-none h-full overflow-y-auto dark:bg-gray-950 dark:text-gray-50">
        <SheetHeader>
          <SheetTitle>{t("book_appointment")}</SheetTitle>
        </SheetHeader>
        <SheetDescription />
        <div className="flex flex-col gap-4 mt-6">
          <Tabs defaultValue="appointment">
            <TabsList className="w-full justify-evenly sm:justify-start border-b rounded-none bg-transparent p-0 h-auto overflow-x-auto">
              <TabsTrigger
                value="appointment"
                className="border-b-3 px-1.5 sm:px-2.5 py-2 text-gray-600 font-semibold hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-50 data-[state=active]:border-b-primary-700 data-[state=active]:text-primary-800 data-[state=active]:dark:text-gray-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none"
              >
                {t("book_appointment")}
              </TabsTrigger>
              <TabsTrigger
                value="encounter"
                className="border-b-3 px-1.5 sm:px-2.5 py-2 text-gray-600 font-semibold hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-50 data-[state=active]:border-b-primary-700 data-[state=active]:text-primary-800 data-[state=active]:dark:text-gray-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none"
              >
                {t("bookings")}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="appointment"
              className="flex lg:flex-row gap-4 mt-2"
            >
              <BookAppointmentDetails
                patientId={patientId}
                onSuccess={() => {
                  setIsOpen(false);
                  onSuccess?.();
                }}
              />
            </TabsContent>
            <TabsContent value="encounter">
              <BookingsList
                patientId={patientId}
                facilityId={facilityId ?? ""}
              />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
